import React, { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = {
        ...state[userIndex],
        ...user,
        isOnline:
          user.isOnline !== undefined ? user.isOnline : state[userIndex].isOnline,
      };
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "UPDATE_USER_PRESENCE") {
    const { userId, isOnline } = action.payload;
    const userIndex = state.findIndex((u) => Number(u.id) === Number(userId));

    if (userIndex !== -1) {
      state[userIndex] = {
        ...state[userIndex],
        isOnline,
      };
    }

    return [...state];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    paddingTop: 10,
    overflow: "auto",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "18px 0 0 0",
    border: "none",
    boxShadow: "none",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      borderRadius: 0,
    },
  },
  actionButton: {
    borderRadius: 999,
    boxShadow: "none",
    textTransform: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  statusWrapper: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  colName: { width: "auto" },
  colId: {
    width: 72,
    whiteSpace: "nowrap",
  },
  colEmail: { width: "auto" },
  colProfile: { width: "auto" },
  colType: { width: "auto" },
  colWhatsapp: { width: "auto" },
  colStatus: { width: "auto" },
  colActions: { width: "auto" },
  actionCell: {
    whiteSpace: "nowrap",
    overflow: "visible",
    textOverflow: "clip",
  },
  statusDotOnline: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    backgroundColor: "#2e7d32",
    display: "inline-block",
    flexShrink: 0,
  },
  statusDotOffline: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    backgroundColor: "#d32f2f",
    display: "inline-block",
    flexShrink: 0,
  },
}));

const Users = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_USERS", payload: data.users });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("user", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    });

    socket.on("userPresence", (data) => {
      dispatch({
        type: "UPDATE_USER_PRESENCE",
        payload: {
          userId: Number(data?.userId),
          isOnline: Boolean(data?.isOnline),
        },
      });
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      <Paper
        className={classes.mainPaper}
        onScroll={handleScroll}
      >
        <MainHeader>
          <Title>{i18n.t("users.title")}</Title>
          <MainHeaderButtonsWrapper>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.actionButton}
              onClick={handleOpenUserModal}
            >
              {i18n.t("users.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.colId}>{i18n.t("users.table.id")}</TableCell>
              <TableCell className={classes.colName}>{i18n.t("users.table.name")}</TableCell>
              <TableCell className={classes.colEmail}>
                {i18n.t("users.table.email")}
              </TableCell>
              <TableCell className={classes.colProfile}>
                {i18n.t("users.table.profile")}
              </TableCell>
              <TableCell className={classes.colType}>
                {i18n.t("users.table.type")}
              </TableCell>
              <TableCell className={classes.colWhatsapp}>
                {i18n.t("users.table.whatsapp")}
              </TableCell>              
              <TableCell className={classes.colStatus}>{i18n.t("users.table.status")}</TableCell>
              <TableCell className={`${classes.colActions} ${classes.actionCell}`}>
                {i18n.t("users.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {users.map((user) => {
                const isUserOnline =
                  user.userType === "ai_agent"
                    ? user.aiAgentProfile?.readinessStatus === "ready"
                    : user.isOnline;

                return (
                  <TableRow key={user.id}>
                    <TableCell className={classes.colId}>{user.id}</TableCell>
                    <TableCell className={classes.colName}>{user.name}</TableCell>
                    <TableCell className={classes.colEmail}>
                      {user.userType === "ai_agent" ? "-" : user.email}
                    </TableCell>
                    <TableCell className={classes.colProfile}>
                      {user.userType === "ai_agent" ? "-" : user.profile}
                    </TableCell>
                    <TableCell className={classes.colType}>
                      {i18n.t(`users.userTypes.${user.userType || "human"}`)}
                    </TableCell>
                    <TableCell className={classes.colWhatsapp}>{user.whatsapp?.name}</TableCell>
                    <TableCell className={classes.colStatus}>
                      <span className={classes.statusWrapper}>
                        <span
                          className={
                            isUserOnline
                              ? classes.statusDotOnline
                              : classes.statusDotOffline
                          }
                        />
                        {isUserOnline
                          ? i18n.t("users.status.online")
                          : i18n.t("users.status.offline")}
                      </span>
                    </TableCell>
                    <TableCell className={`${classes.colActions} ${classes.actionCell}`}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setConfirmModalOpen(true);
                          setDeletingUser(user);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {loading && <TableRowSkeleton columns={8} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Users;
